import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api-client.js';
import type { AdminAccount } from '../pages/AdminPage.js';

export interface AuditLogEntry {
  id: string;
  accountId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  payload: unknown;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

const ACTION_META: Record<string, { icon: string; label: string }> = {
  // Authentication
  'auth.login_success': { icon: '✓', label: 'Login successful' },
  'auth.login_failed':  { icon: '✗', label: 'Login failed' },
  'auth.login_discord': { icon: '◈', label: 'Discord login' },
  'auth.logout':        { icon: '→', label: 'Logout' },
  'auth.register':      { icon: '✦', label: 'Registration' },
  // Admin
  'admin.set_role':       { icon: '⚑', label: 'Role change' },
  'admin.set_banned':     { icon: '⊘', label: 'Ban' },
  'admin.delete_account': { icon: '✕', label: 'Account deleted' },
  'admin.set_password':   { icon: '●', label: 'Password changed' },
  'admin.create_account': { icon: '✦', label: 'Account created' },
  'admin.foxhole.refresh':{ icon: '↻', label: 'Foxhole cache flushed' },
  // Map objects
  'map.object.created': { icon: '✦', label: 'Map marker created' },
  'map.object.deleted': { icon: '✕', label: 'Map marker deleted' },
  'map.object.updated': { icon: '✎', label: 'Map marker updated' },
  // Map arrows
  'map.arrow.created': { icon: '➜', label: 'Arrow drawn' },
  'map.arrow.deleted': { icon: '✕', label: 'Arrow erased' },
  // War-plan & map settings
  'war-plan.updated':     { icon: '⚔', label: 'War plan updated' },
  'map-settings.updated': { icon: '⚙', label: 'Map settings updated' },
  // Legacy action names (backward compat for existing log entries)
  'auth.login':            { icon: '✓', label: 'Login' },
  'admin.user.create':     { icon: '✦', label: 'Account created' },
  'admin.user.delete':     { icon: '✕', label: 'Account deleted' },
  'admin.user.role':       { icon: '⚑', label: 'Role change' },
  'admin.user.ban':        { icon: '⊘', label: 'Banned' },
  'admin.user.unban':      { icon: '⊙', label: 'Unbanned' },
  'admin.user.reset_password': { icon: '●', label: 'Password reset' },
};

function formatRelative(iso: string): { label: string; full: string } {
  const full = new Date(iso).toLocaleString();
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1)  return { label: 'just now', full };
  if (mins < 60) return { label: `${mins} min ago`, full };
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return { label: `${hrs}h ago`, full };
  if (hrs < 48)  return { label: 'yesterday', full };
  return { label: new Date(iso).toLocaleDateString(), full };
}

function parseUA(ua: string | null): string {
  if (!ua) return '';
  const browsers: [RegExp, string][] = [
    [/Edg\/[\d.]+/, 'Edge'],
    [/OPR\/[\d.]+/, 'Opera'],
    [/Chrome\/([\d.]+)/, 'Chrome'],
    [/Firefox\/([\d.]+)/, 'Firefox'],
    [/Safari\/([\d.]+)/, 'Safari'],
  ];
  let browser = '';
  for (const [re, name] of browsers) if (re.test(ua)) { browser = name; break; }
  const oses: [RegExp, string][] = [
    [/Windows NT/, 'Windows'],
    [/Mac OS X/, 'macOS'],
    [/Android/, 'Android'],
    [/iPhone|iPad|iOS/, 'iOS'],
    [/Linux/, 'Linux'],
  ];
  let os = '';
  for (const [re, name] of oses) if (re.test(ua)) { os = name; break; }
  return [browser, os].filter(Boolean).join(' / ') || ua.slice(0, 40);
}

function summarizePayload(action: string, payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  if (action === 'admin.set_role' && typeof p['role'] === 'string') return `→ ${p['role']}`;
  if (action.includes('role') && typeof p['role'] === 'string') return `→ ${p['role']}`;
  if (action === 'admin.set_banned') return p['banned'] ? 'banned' : 'unbanned';
  if (action === 'admin.user.ban') return 'banned';
  if (action === 'admin.user.unban') return 'unbanned';
  if (action === 'auth.login_failed' && typeof p['pseudo'] === 'string') return `username: ${p['pseudo']}`;
  if (
    (action === 'map.object.created' || action === 'map.object.deleted' || action === 'map.object.updated') &&
    typeof p['kind'] === 'string'
  ) {
    const KIND_LABELS: Record<string, string> = {
      combat:   'Combat',
      build:    'Build',
      facility: 'Facility',
      logistic: 'Logistic',
      navy:     'Navy',
    };
    return KIND_LABELS[p['kind']] ?? p['kind'];
  }
  if (action === 'map.arrow.created' || action === 'map.arrow.deleted') {
    const ARROW_KIND_LABELS: Record<string, string> = {
      movement: 'Movement',
      attack:   'Attack',
      defense:  'Defense',
      supply:   'Supply',
      retreat:  'Retreat',
    };
    const kind = typeof p['kind'] === 'string' ? (ARROW_KIND_LABELS[p['kind']] ?? p['kind']) : null;
    const pts = typeof p['pointCount'] === 'number' ? `${p['pointCount']} pts` : null;
    const parts = [kind, pts].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : null;
  }
  if (action === 'war-plan.updated') {
    const WAR_STYLE: Record<string, string> = {
      offensive: 'Offensive',
      balanced:  'Balanced',
      defensive: 'Defensive',
    };
    const FREQ: Record<string, string> = { high: 'High', medium: 'Medium', low: 'Low' };
    const STOCK: Record<string, string> = {
      frontline_supply: 'Frontline supply',
      balanced_use:     'Balanced use',
    };
    const RISK: Record<string, string> = { high: 'High', medium: 'Medium', low: 'Low' };
    const bits: string[] = [];
    if (typeof p['title'] === 'string') bits.push(`title → “${p['title']}”`);
    if (typeof p['warStyle'] === 'string') bits.push(`style → ${WAR_STYLE[p['warStyle']] ?? p['warStyle']}`);
    if (typeof p['opFrequency'] === 'string') bits.push(`ops → ${FREQ[p['opFrequency']] ?? p['opFrequency']}`);
    if (typeof p['stockManagement'] === 'string') bits.push(`stock → ${STOCK[p['stockManagement']] ?? p['stockManagement']}`);
    if (typeof p['griefingRisk'] === 'string') bits.push(`griefing → ${RISK[p['griefingRisk']] ?? p['griefingRisk']}`);
    return bits.length > 0 ? bits.join(' · ') : null;
  }
  if (action === 'map-settings.updated' && Array.isArray(p['disabledRegionKeys'])) {
    const n = (p['disabledRegionKeys'] as unknown[]).length;
    if (n === 0) return 'all regions enabled';
    return `${n} region${n > 1 ? 's' : ''} disabled`;
  }
  return null;
}

function resolveTarget(
  targetType: string | null,
  targetId: string | null,
  all: AdminAccount[],
): string | null {
  if (!targetType || !targetId) return null;
  if (targetType === 'account') {
    const found = all.find((a) => a.id === targetId);
    return found ? found.pseudo : `#${targetId.slice(0, 8)}`;
  }
  if (targetType === 'map_object') return null; // kind affiché via summarizePayload
  return `${targetType} #${targetId.slice(0, 8)}`;
}

export interface AuditLogModalProps {
  account: AdminAccount;
  allAccounts: AdminAccount[];
  open: boolean;
  onClose: () => void;
}

const LIMIT = 25;

export function AuditLogModal({
  account,
  allAccounts,
  open,
  onClose,
}: AuditLogModalProps): React.JSX.Element {
  const [limit, setLimit] = useState(LIMIT);
  const [authOpen, setAuthOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(true);
  const [activityOpen, setActivityOpen] = useState(true);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Reset pagination when the subject or open state changes
  useEffect(() => {
    if (open) setLimit(LIMIT);
  }, [open, account.id]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const query = useQuery({
    queryKey: ['admin', 'audit', account.id, limit],
    queryFn: () =>
      api.get<AuditLogEntry[]>(
        `/api/admin/accounts/${encodeURIComponent(account.id)}/audit-logs?limit=${limit}&offset=0`,
      ),
    enabled: open,
  });

  const entries = query.data ?? [];
  const hasMore = entries.length === limit;

  const authEntries = entries.filter((e) => e.action.startsWith('auth.'));
  const adminEntries = entries.filter((e) => e.action.startsWith('admin.'));
  const activityEntries = entries.filter(
    (e) => !e.action.startsWith('auth.') && !e.action.startsWith('admin.'),
  );

  // Formatted dates for header
  const createdRel = formatRelative(account.createdAt);
  const lastSeenRel = formatRelative(account.lastSeenAt);

  if (!open) return <></>;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded border border-slate-800 bg-slate-900 text-slate-100 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="shrink-0 border-b border-slate-800 px-5 py-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-semibold">{account.pseudo}</span>
                {account.discordId !== null ? (
                  <span className="rounded bg-indigo-800 px-2 py-0.5 text-xs">Discord</span>
                ) : (
                  <span className="rounded bg-slate-700 px-2 py-0.5 text-xs">Local</span>
                )}
                {account.role === 'admin' ? (
                  <span className="rounded bg-amber-700 px-2 py-0.5 text-xs uppercase">ADMIN</span>
                ) : (
                  <span className="rounded bg-slate-700 px-2 py-0.5 text-xs uppercase">USER</span>
                )}
                {account.bannedAt && (
                  <span className="rounded bg-red-700 px-2 py-0.5 text-xs">banned</span>
                )}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Created{' '}
                <span title={createdRel.full}>{createdRel.label}</span>
                {' · '}Last seen{' '}
                <span title={lastSeenRel.full}>{lastSeenRel.label}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            >
              ✕
            </button>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm">
          {query.isLoading && <p className="text-slate-400">Loading…</p>}
          {query.isError && <p className="text-red-400">{query.error instanceof Error ? query.error.message : 'Error'}</p>}

          {query.data && (
            <>
              {/* AUTHENTICATION section */}
              <Section
                title="AUTHENTICATION"
                count={authEntries.length}
                open={authOpen}
                onToggle={() => setAuthOpen((v) => !v)}
              >
                {authEntries.map((e) => (
                  <EntryRow
                    key={e.id}
                    entry={e}
                    subject={account}
                    allAccounts={allAccounts}
                  />
                ))}
                {authEntries.length === 0 && (
                  <p className="px-3 py-2 text-xs text-slate-500">No entries.</p>
                )}
              </Section>

              {/* ADMIN section */}
              <Section
                title="ADMIN ACTIONS"
                count={adminEntries.length}
                open={adminOpen}
                onToggle={() => setAdminOpen((v) => !v)}
              >
                {adminEntries.map((e) => (
                  <EntryRow
                    key={e.id}
                    entry={e}
                    subject={account}
                    allAccounts={allAccounts}
                  />
                ))}
                {adminEntries.length === 0 && (
                  <p className="px-3 py-2 text-xs text-slate-500">No entries.</p>
                )}
              </Section>

              {/* ACTIVITY section (map markers, arrows, war-plan, map settings) */}
              <Section
                title="MAP ACTIVITY"
                count={activityEntries.length}
                open={activityOpen}
                onToggle={() => setActivityOpen((v) => !v)}
              >
                {activityEntries.map((e) => (
                  <EntryRow
                    key={e.id}
                    entry={e}
                    subject={account}
                    allAccounts={allAccounts}
                  />
                ))}
                {activityEntries.length === 0 && (
                  <p className="px-3 py-2 text-xs text-slate-500">No entries.</p>
                )}
              </Section>

              {hasMore && (
                <div className="mt-3 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setLimit((l) => l + LIMIT)}
                    className="rounded bg-slate-800 px-4 py-1.5 text-xs hover:bg-slate-700"
                  >
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  count,
  open,
  onToggle,
  children,
}: {
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 rounded px-1 py-1.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200"
      >
        <span className="flex-1">{title}</span>
        <span className="rounded bg-slate-700 px-1.5 py-0.5 font-mono normal-case tracking-normal text-slate-300">
          {count}
        </span>
        <span className="text-slate-500">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

function EntryRow({
  entry: e,
  subject,
  allAccounts,
}: {
  entry: AuditLogEntry;
  subject: AdminAccount;
  allAccounts: AdminAccount[];
}): React.JSX.Element {
  const meta = ACTION_META[e.action] ?? { icon: '·', label: e.action };
  const ts = formatRelative(e.createdAt);
  const ua = parseUA(e.userAgent);
  const summary = summarizePayload(e.action, e.payload);
  const target = resolveTarget(e.targetType, e.targetId, allAccounts);

  // "Received" perspective: action was done TO this account by someone else
  const isReceived =
    e.targetType === 'account' &&
    e.targetId === subject.id &&
    e.accountId !== null &&
    e.accountId !== subject.id;

  const actorPseudo = isReceived
    ? (allAccounts.find((a) => a.id === e.accountId)?.pseudo ?? `#${(e.accountId ?? '').slice(0, 8)}`)
    : null;

  return (
    <div
      className={`grid grid-cols-[1.25rem_1fr_auto] gap-x-2 gap-y-0.5 px-3 py-2 odd:bg-slate-800/40 ${
        isReceived ? 'border-l-2 border-indigo-600/50' : ''
      }`}
    >
      {/* Icon */}
      <span className="font-mono text-xs text-slate-400 mt-0.5">{meta.icon}</span>

      {/* Main content */}
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="font-medium text-slate-100">{meta.label}</span>
        {isReceived && actorPseudo && (
          <span className="text-xs text-indigo-400">by {actorPseudo}</span>
        )}
        {!isReceived && target && (
          <span className="text-xs text-slate-400">{target}</span>
        )}
        {summary && (
          <span className="rounded bg-slate-700/60 px-1.5 py-0.5 text-xs text-slate-300">
            {summary}
          </span>
        )}
        <span className="flex w-full items-center gap-2 mt-0.5">
          {e.ip && (
            <span className="font-mono text-xs text-slate-500">{e.ip}</span>
          )}
          {ua && (
            <span className="cursor-default text-xs text-slate-600" title={e.userAgent ?? undefined}>
              {ua}
            </span>
          )}
        </span>
      </span>

      {/* Timestamp */}
      <span
        className="mt-0.5 whitespace-nowrap text-xs text-slate-500"
        title={ts.full}
      >
        {ts.label}
      </span>
    </div>
  );
}
